import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom';
import { getClosingOverlay, setClosingOverlay, schedulePop, markEntryDead, startHousekeeping } from './overlayHistoryStore';
import { useHoldPageScroll } from './holdPageScroll';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  /** 'sheet' anchors to the bottom edge and slides up — same dialog semantics,
   *  different geometry. Added so a bottom sheet does not have to be a second
   *  modal implementation: the focus trap, the Escape handler and the ARIA all
   *  live here once, and a copy of them would drift from this one (JAR-1076). */
  variant?: 'center' | 'sheet';
  /** Chrome rendered above the scroll area rather than inside it, for a sheet
   *  whose header is not a title bar. */
  header?: ReactNode;
  /** Accessible name when there is no visible title. */
  ariaLabel?: string;
}

type BrowserEntry = { idx?: unknown; key?: unknown; usr?: { overlay?: string; [field: string]: unknown } } | null;
const browserEntry = () => window.history.state as BrowserEntry;
/** The router keeps its own index on every browser entry; a MemoryRouter never touches window.history. */
const drivesBrowserHistory = () => typeof browserEntry()?.idx === 'number';
const overlayOnTop = () => browserEntry()?.usr?.overlay;


/**
 * Back closes an open overlay instead of leaving the screen (JAR-1412).
 *
 * The back swipe is how a phone dismisses a sheet, but no overlay was in the
 * history, so Back popped the screen itself and the overlay went with it.
 * Opening now pushes a same-URL entry that names this overlay; Back pops it and
 * the overlay closes. Closing any other way (X, scrim, Escape, an action) takes
 * the entry back off, so the next Back still leaves the screen in one step.
 *
 * An overlay opened on top of another pushes its own entry above it, and the
 * one underneath stays open: each closes only when Back pops below its own
 * entry. A custom sheet that isn't a Modal renders this too (the Jarvis planner).
 */
export function OverlayHistory({ onClose }: { onClose: () => void }) {
  const id = useId();
  const location = useLocation();
  const navigate = useNavigate();
  // Where the overlay opened, and a navigate from then: a same-URL push and a
  // -1 pop mean the same thing for the overlay's whole life.
  const [openedAt] = useState(() => location);
  const [nav] = useState(() => navigate);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const alive = useRef(false);
  // The router's index for this overlay's own entry. An overlay opening on top
  // pushes above it; only a pop below it means Back closed this one.
  const ownIdx = useRef<number | null>(null);
  const ownKey = useRef<string | null>(null);

  // Puts this overlay's entry on top of history, unless it's there already, and
  // notes where it stands.
  const holdEntry = useCallback((replace: boolean) => {
    if (overlayOnTop() !== id) {
      const { pathname, search, hash, state } = openedAt;
      nav(`${pathname}${search}${hash}`, { replace, state: { ...((state ?? {}) as Record<string, unknown>), overlay: id } });
    }
    const idx = browserEntry()?.idx;
    ownIdx.current = typeof idx === 'number' ? idx : null;
    const key = browserEntry()?.key;
    ownKey.current = typeof key === 'string' ? key : null;
  }, [id, nav, openedAt]);

  useEffect(() => {
    if (!drivesBrowserHistory()) return;
    alive.current = true;
    const closing = getClosingOverlay();
    const handOver = closing !== null && overlayOnTop() === closing;
    if (handOver) setClosingOverlay(null);
    holdEntry(handOver);
    return () => {
      alive.current = false;
      if (overlayOnTop() !== id) {
        // Buried: another overlay, or a page the closing action navigated to, sits
        // on this entry, and history can only take off the top. Marked, so the
        // Back that lands on it keeps going instead of doing nothing.
        const cur = browserEntry()?.idx;
        if (typeof cur === 'number' && ownIdx.current !== null && cur > ownIdx.current && ownKey.current) markEntryDead(ownKey.current, cur);
        return;
      }
      setClosingOverlay(id);
      // Deferred a tick: an overlay opening in this same commit takes the entry
      // over (above), and a StrictMode re-mount keeps it. Re-read the live entry
      // then: an action that closed the overlay and navigated has pushed its own,
      // and popping that would undo the navigation.
      schedulePop(() => {
        if (getClosingOverlay() === id) setClosingOverlay(null);
        if (!alive.current && overlayOnTop() === id) {
          // What the screen wrote onto this entry while the overlay was open (the
          // Dashboard's tab) goes down with the pop, onto the entry it lands on.
          const top = browserEntry();
          const screenState: Record<string, unknown> = { ...(top?.usr ?? {}) };
          delete screenState.overlay;
          const { pathname, search, hash } = openedAt;
          startHousekeeping((landed) => {
            if (Object.keys(screenState).every((k) => landed[k] === screenState[k])) return;
            nav(`${pathname}${search}${hash}`, { replace: true, state: { ...landed, ...screenState } });
          }, typeof top?.idx === 'number' ? top.idx : 0);
          nav(-1);
        }
      });
    };
  }, [id, nav, openedAt, holdEntry]);

  // Back popped this overlay's entry: close it. Comparing indexes, rather than
  // asking whose entry is on top, is what lets another overlay open over this one.
  const [backs, setBacks] = useState(0);
  useEffect(() => {
    const idx = browserEntry()?.idx;
    if (ownIdx.current !== null && typeof idx === 'number' && idx < ownIdx.current) {
      onCloseRef.current();
      setBacks((n) => n + 1);
    }
  }, [location]);
  // Still here once that close has rendered: the overlay refused it, as a dialog
  // does while it saves. Back took its entry anyway, so the entry goes back on,
  // or the next Back would leave the screen with the overlay still up. A close
  // unmounts this in the same render, so this runs only after a refusal.
  useEffect(() => { if (backs > 0) holdEntry(false); }, [backs, holdEntry]);

  return null;
}

// The dialogs open now, innermost last. Only the top one answers Escape and
// keeps Tab: a confirmation opened over a sheet is what Escape closes, not the
// sheet too, and the sheet's trap would pull focus out of the confirmation.
// Every one under it is inert, so a screen reader can't wander into the sheet
// either. `root` is the dialog's portal root.
// ponytail: ordered by when each opened, so a dialog mounted in the same commit
// as one inside it lands under it; nest depth through context if that matters.
type OpenDialog = { root: HTMLElement | null };
const openDialogs: OpenDialog[] = [];

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  variant = 'center',
  header,
  ariaLabel,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const inRouter = useInRouterContext();
  // This dialog's entry on the stack. Nothing renders from it, so it's a ref.
  const self = useRef<OpenDialog>({ root: null });
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // Keyed on isOpen only, so a parent's new inline onClose on each render can't
  // take this dialog off the stack and put it back on top of one opened over it.
  useEffect(() => {
    if (!isOpen) return;
    const dialog = self.current;
    dialog.root = rootRef.current;
    openDialogs[openDialogs.length - 1]?.root?.setAttribute('inert', '');
    openDialogs.push(dialog);
    return () => {
      const i = openDialogs.indexOf(dialog);
      if (i < 0) return;
      openDialogs.splice(i, 1);
      // This one was on top, so the one it covered is reachable again.
      if (i === openDialogs.length) openDialogs[i - 1]?.root?.removeAttribute('inert');
    };
  }, [isOpen]);

  // Move focus into the dialog on open (keyboard / SR users land inside it)
  // and hand it back to the trigger on close — otherwise closing strands
  // keyboard users at the top of the document (JAR-290). Keyed on isOpen
  // ONLY — re-running on every render would steal focus back from a
  // controlled input mid-typing.
  useEffect(() => {
    if (!isOpen) return;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    return () => { if (trigger?.isConnected) trigger.focus(); };
  }, [isOpen]);

  // Keep Tab inside the dialog while it's open — aria-modal tells assistive
  // tech the background doesn't exist, so the keyboard must not reach it
  // (JAR-290). Wraps at both ends; a focus that escaped anyway is pulled back.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || openDialogs[openDialogs.length - 1] !== self.current) return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), details>summary:not([disabled]), [contenteditable]:not([disabled]), audio[controls], video[controls]',
      );
      if (focusables.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (!panel.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // The page behind stays still under a mouse wheel too (JAR-3017).
  useHoldPageScroll(isOpen);

  // Escape closes the dialog on top only.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openDialogs[openDialogs.length - 1] === self.current) onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeStyles = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full mx-4',
  };

  const sheet = variant === 'sheet';
  const panelStyles = sheet
    ? 'rounded-t-2xl border-t border-x border-border max-w-[440px] w-full max-h-[92dvh] pb-[env(safe-area-inset-bottom)] animate-sheet-up'
    : `rounded-2xl border border-border ${sizeStyles[size]} w-full mx-4 max-h-[90dvh] animate-modal-in`;

  // Portaled to <body>, like CoachMark (JAR-1395). A `position: fixed` layer
  // still honours its own margin, and an ancestor's transform or overflow
  // re-frames it; at <body> no mount site can (JAR-1408). Theme and type still
  // apply: the `dark` class and the font live on <html>, the colours on <body>.
  return createPortal(
    <div ref={rootRef} className={`fixed inset-0 z-50 flex justify-center ${sheet ? 'items-end' : 'items-center'}`}>
      {inRouter && <OverlayHistory onClose={onClose} />}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        data-modal
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel || 'Dialog'}
        tabIndex={-1}
        className={`relative bg-surface text-body shadow-xl overflow-hidden flex flex-col outline-none ${panelStyles}`}
      >
        {header}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border/60">
            {title && <h2 id={titleId} className="text-base font-semibold text-ink truncate">{title}</h2>}
            {showCloseButton && (
              <button
                onClick={onClose}
                aria-label="Close"
                className="shrink-0 -mr-1 p-1.5 text-muted hover:text-ink hover:bg-surface-raised rounded-full transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
            )}
          </div>
        )}
        {/* overscroll-contain: a swipe past the end of the sheet stops here, rather than
            scrolling the page behind it (JAR-1478). */}
        <div className="overflow-y-auto overscroll-contain px-4 py-4">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
